/**
 * Links
 *
 * What the package's chips link to — a record, a tag — and how an app turns that
 * into a URL. The package knows no routes: an app registers, once at startup, the
 * component that navigates (usually its router's link) and a function from a
 * target to what that component takes as `to`. Until it does, `AugerLink` renders
 * its content with no navigation.
 */
import type { Component } from "vue"

/** What a chip links to. A list is a record of type `list`. */
export type AugerLinkTarget =
  | { kind: "record"; type: string; slug?: string | null; name?: string | null }
  | { kind: "tag"; slug?: string | null }

export interface AugerLinks {
  /** The component that navigates, rendered with a `to` prop (e.g. the router's link). */
  component?: Component
  /** The `to` for a target: a route location or a URL. */
  resolve: (target: AugerLinkTarget) => unknown
}

/** The link target for a record row, or null for anything without a `type`. */
export function recordTarget(
  record: { type?: unknown; slug?: unknown; name?: unknown } | null | undefined
): AugerLinkTarget | null {
  if (!record || typeof record.type !== "string") return null
  return {
    kind: "record",
    type: record.type,
    slug: typeof record.slug === "string" ? record.slug : null,
    name: typeof record.name === "string" ? record.name : null,
  }
}

/** The link target for a tag row. */
export function tagTarget(tag: { slug?: unknown } | null | undefined): AugerLinkTarget {
  return { kind: "tag", slug: typeof tag?.slug === "string" ? tag.slug : null }
}

let current: AugerLinks | null = null

/** Set how the package's chips navigate. Call once, at startup. Null clears it. */
export function registerAugerLinks(links: AugerLinks | null): void {
  current = links
}

/** The registered links, or null before an app registers any. */
export function getAugerLinks(): AugerLinks | null {
  return current
}
